import { translate } from "app/language/service"
import { APP_CONTEXTS, NOT_SAVED_ID } from "app/model"
import { getActiveAppContext, setActiveAppContext } from "app/tools/common"
import { CAMPAIGN_STATUSES } from "campaign/model"
import { isCampaignEditable } from "campaign/tools/edit"
import { FORM_SAVE_ERRORS, IAppForm, IForm, IFormError } from "form/model"
import { notifyFormClosed, notifyFormOpened } from "form/tools/common"
import { loadCampaignForEdit } from "form/tools/edit"
import { getTabIdInUrl } from "form/tools/history"
import { NotFoundPage } from "page/components/NotFoundPage"
import { UnexpectedErrorPage } from "page/components/UnexpectedErrorPage"
import React from "react"
import { useParams } from "react-router-dom"
import { FormLayout } from "../layout/FormLayout"

interface IEditPageProps {
  context: APP_CONTEXTS | null
  isLoadingInitialData: boolean
  hasInitialDataLoadingError: boolean
  isLoading: boolean
  error: IFormError
  saveError: FORM_SAVE_ERRORS | null
  status: CAMPAIGN_STATUSES | null
  appForm: IAppForm
  isForecast: boolean
  isDspDeal: boolean
}

export const EditPage = React.memo(
  ({
    context,
    isLoadingInitialData,
    hasInitialDataLoadingError,
    isLoading,
    error,
    saveError,
    status,
    appForm,
    isForecast,
    isDspDeal
  }: IEditPageProps) => {
    const { campaignId: inUrlCampaignId } = useParams()
    const form: IForm = appForm.form
    const inStoreCampaignId: number = form.campaignId
    React.useEffect(() => {
      if (
        (!isForecast && !isDspDeal && context !== APP_CONTEXTS.CAMPAIGN_EDIT) ||
        (isForecast && context !== APP_CONTEXTS.FORECAST_EDIT) ||
        (isDspDeal && context !== APP_CONTEXTS.DSP_DEAL_EDIT)
      ) {
        setActiveAppContext(
          isForecast ? APP_CONTEXTS.FORECAST_EDIT : isDspDeal ? APP_CONTEXTS.DSP_DEAL_EDIT : APP_CONTEXTS.CAMPAIGN_EDIT
        )
        return
      }
      const toLoadCampaignId = Number(inUrlCampaignId)
      const isLoaded = () => {
        const activeContext = getActiveAppContext()
        return (
          ((!isForecast && !isDspDeal && activeContext === APP_CONTEXTS.CAMPAIGN_EDIT) ||
            (isForecast && activeContext === APP_CONTEXTS.FORECAST_EDIT) ||
            (isDspDeal && activeContext === APP_CONTEXTS.DSP_DEAL_EDIT)) &&
          toLoadCampaignId === inStoreCampaignId
        )
      }
      const onClose = () => {
        if (getActiveAppContext()) {
          notifyFormClosed(form)
        }
      }
      if (inUrlCampaignId === "" || inUrlCampaignId === undefined || isLoading || isLoaded()) {
        return onClose
      }
      // loadCampaignForEdit will change inStoreCampaignId, and so isLoaded will become true
      loadCampaignForEdit(toLoadCampaignId, form.statusId, isForecast, isDspDeal, getTabIdInUrl()).then(newForm =>
        notifyFormOpened(newForm)
      )
      return onClose
    }, [context])
    if (!context) {
      return null
    }
    const isEditable = status ? isCampaignEditable(status) : true
    return !isEditable ? (
      <UnexpectedErrorPage message={translate("campaign.not.editable")} />
    ) : appForm.error === "not-found" ? (
      <NotFoundPage
        message={translate(
          isForecast ? "forecast.status.error" : isDspDeal ? "dsp-deal.status.error" : "campaign.status.error"
        )}
      />
    ) : appForm.error === "loading" ? (
      <UnexpectedErrorPage message={translate("form.load.error")} />
    ) : form &&
      form.campaignId !== NOT_SAVED_ID &&
      form.tempCampaignId !== NOT_SAVED_ID &&
      !isLoading &&
      status &&
      status === CAMPAIGN_STATUSES.TEMPORARY ? (
      <NotFoundPage
        message={translate(
          isForecast ? "forecast.status.error" : isDspDeal ? "dsp-deal.status.error" : "campaign.status.error"
        )}
      />
    ) : (
      <FormLayout
        form={form}
        status={status}
        hasInitialDataLoadingError={hasInitialDataLoadingError}
        isLoadingInitialData={isLoadingInitialData}
        isLoading={isLoading}
        error={error}
        saveError={saveError}
      />
    )
  }
)
